import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
  GridColDef,
  GridSlots,
} from '@mui/x-data-grid';
import { IParams } from '../../ProgramaAtual';
import { useRecoilState, useRecoilValue } from 'recoil';
import { comandaAtom } from '../../../../../states/comandas/ComandaState';
import { useAnexosActions } from '../../../../../states/contatos/auxiliares/anexos/anexos.actions';
import InputArquivo from '../../../../../components/InputArquivo/InputArquivo';
import { useComandaActions } from '../../../../../states/comandas/comanda.actions';
import { useTranslation } from '../../../../../commons/useTranslasion';
import { useDateFormatters } from '../../../../../utils/useDateFormatters';
import { esES, ptBR } from '@mui/material/locale';

interface EditToolbarProps {
  onAddClick: () => void;
}

function EditToolbar(props: EditToolbarProps) {
  const { translate } = useTranslation();

  const { onAddClick } = props;

  return (
    <Box sx={{ padding: 1 }}>
      <Button color="primary" startIcon={<AddIcon />} onClick={onAddClick}>
        Adicionar Novo Anexo
      </Button>
    </Box>
  );
}

const AnexosDetalhes = () => {
  const { translate, language } = useTranslation();
  const { formatarData } = useDateFormatters();
  const lang = language ?? "pt-br";
  let locale = ptBR;
  switch (lang.slice(0, 2)) {
    case "es":
      locale = esES
      break;
  }

  const { id } = useParams<IParams>();
  const comandaActions = useComandaActions();
  const comandaValue = useRecoilValue(comandaAtom);
  const comanda = comandaValue.find((f) => f.id === Number(id));
  const [contatoId, setContatoId] = useState<number | undefined>(comanda?.contato?.id);
  const anexosActions = useAnexosActions();
  const [anexos, setAnexos] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [novoArquivo, setNovoArquivo] = useState<any>({ anexo: '' } as any);
  const [nomeArquivo, setNomeArquivo] = useState('');
  const [arquivoUrl, setArquivoUrl] = useState('');

  useEffect(() => {
    if (contatoId)
      getAnexos(contatoId);
    else {
      carregarComanda();
    }
  }, [id]);

  const getAnexos = (contato: any) => {
    anexosActions
      .list(false, `?contatoId=${contato}`)
      .then((data) => setAnexos(data))
      .catch((err) => toast.error(err));
  }

  const carregarComanda = async () => {
    const comandaGet = await comandaActions.get(Number(id));
    setContatoId(comandaGet.contato!.id);
    getAnexos(comandaGet.contato!.id);
    return comandaGet;
  }

  const handleAddClick = () => {
    setIsDialogOpen(true);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setNomeArquivo('');
    setArquivoUrl('');
    setNovoArquivo({ anexo: '' } as any);
  };

  const handleDeleteClick = (id: number) => () => {
    anexosActions.delete(id).then(() => {
      setAnexos((prevRows) => prevRows.filter((row) => row.id !== id));
    });
  };

  const handleDownloadClick = (arquivoLink: string) => () => {
    window.open(arquivoLink, '_blank');
  };

  const handleSaveAnexo = () => {
    const obj = {
      idContato: contatoId,
      arquivoFile: novoArquivo.anexo,
    };
    anexosActions
      .createFormData(obj)
      .then((resp) => {
        setAnexos([...anexos, resp]);
        toast.success(translate('anexoSalvoComSucesso'));
      })
      .catch((err) => toast.error(err))
      .finally(() => handleDialogClose());
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'arquivo', headerName: translate('anexo'), minWidth: 350, flex: 1 },
    {
      field: 'dtCadastro',
      headerName: translate('dataDeCadastro'),
      width: 200,
      valueGetter: (value) => {
        return value;
      },
      renderCell: (params: any) => {
        // formatarData será obtido do componente pai
        return formatarData(params.value);
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: translate('acoes'),
      width: 100,
      cellClassName: 'actions',
      getActions: (props: any) => [
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label={translate('deletar')}
          onClick={handleDeleteClick(props.id)}
          color="inherit"
        />,
        <GridActionsCellItem
          icon={<DownloadIcon />}
          label={translate('download')}
          onClick={handleDownloadClick(props.row.arquivoLink)}
          color="primary"
        />,
      ],
    },
  ];

  return (
    <>
      <Box
        sx={{
          height: 400,
          width: '100%',
        }}
      >
        <ThemeProvider theme={createTheme({}, locale)}>
          <DataGrid
            rows={anexos}
            columns={columns}
            slots={{
              toolbar: EditToolbar as GridSlots['toolbar'],
            }}
            slotProps={{
              toolbar: { onAddClick: handleAddClick },
            }}
          />
        </ThemeProvider>

        <Dialog open={isDialogOpen} onClose={handleDialogClose}>
          <DialogTitle>{translate('adicionarNovoAnexo')}</DialogTitle>
          <DialogContent>
            <InputArquivo
              isEdit={false}
              newObj={novoArquivo}
              setNewObj={setNovoArquivo}
              nameFile={nomeArquivo}
              setNameFile={setNomeArquivo}
              urlFile={arquivoUrl}
              setUrlFile={setArquivoUrl}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="secondary">
              {translate('cancelar')}
            </Button>
            <Button onClick={handleSaveAnexo} color="primary">
              {translate('enviar')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}

export default AnexosDetalhes;
